import type {
  ModelInventoryChange,
  ModelWarehouseInventoryState,
} from '@app/api/WarehouseInventories';
import type { ProductLeftoverData } from '@app/pages/Warehouses/components/ProductLeftover/types';

import type { PaginationProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd/lib';
import type { FC } from 'react';

export enum InProgressConditionEnum {
  ALL = 'all',
  COUNTED = 'counted',
  NOT_COUNTED = 'not_counted',
}

export enum DefaultConditionEnum {
  ALL = 'all',
  EXCESS = 'excess',
  DEFICIENCY = 'deficiency',
  NORMAL = 'normal',
}

export enum SortByEnum {
  NAME = 'name',
  ALL_COUNT = 'all_count',
  COUNTED = 'counted',
}
export interface InventoryCardFilters<
  T extends ModelWarehouseInventoryState | null | undefined
> {
  search?: string;
  condition?: T extends ModelWarehouseInventoryState.Open
    ? InProgressConditionEnum
    : DefaultConditionEnum;
  sortBy?: SortByEnum;
}

interface HeaderProps {
  date?: string | null;
  warehouseName?: string | null;
  comment?: string | null;
  status?: ModelWarehouseInventoryState | null;
  progress?: number | null;
}

export type ConditionOption = {
  value: InProgressConditionEnum | DefaultConditionEnum;
  label: string;
};

export type OnChangeFiltersType = (
  key: keyof InventoryCardFilters<ModelWarehouseInventoryState>,
  value: InventoryCardFilters<ModelWarehouseInventoryState>[typeof key]
) => void;

export interface FiltersProps {
  values: InventoryCardFilters<ModelWarehouseInventoryState>;
  onChangeFilters: OnChangeFiltersType;
  conditionOptions: ConditionOption[];
}

export interface Pagination {
  itemsPerPage: number;
  pageN: number;
  total: number;
  from: number;
  to: number;
}

export interface ProductsProps {
  isLoading?: boolean;
  isFetching?: boolean;
  data: InventoryProduct[];
  columns: ColumnsType<InventoryProduct>;
  pagination: Pagination;
  onPaginate: PaginationProps['onChange'];
  onPageSizeChange(itemsPerPage: number): void;
}

interface GetInventoryProductsColumnsProps {
  inProgress: boolean;
  dropdownItems: MenuProps['items'];
  onDropdownClick(id: number): MenuProps['onClick'];
  getProductCountValue: (
    id: number,
    serverValue: number | null
  ) => number | null;
  onProductCountChange: (id: number, value: number | null) => void;
  commitProductCount: (id: number, serverValue: number | null) => Promise<void>;
  status: ModelWarehouseInventoryState | null | undefined;
}

export type GetInventoryProductsColumnsType = (
  props: GetInventoryProductsColumnsProps
) => ColumnsType<InventoryProduct>;
export interface InventoryProduct {
  id: number;
  name: string;
  countWarehouse: number | null;
  countInventory: number | null;
  countDifference: number | null;
  countDifferencePercent: number | null;
  costDifference: string | number | null;
  difference: number | null;
  leftover?: ProductLeftoverData;
}

export interface HistoryItem {
  id: number;
  productName: string;
  counted: number;
  timestamp: string;
}

interface HistoryProps {
  list: HistoryItem[];
}

export interface WsEvent {
  jsonData: string;
  isSuccess: boolean;
}

export interface UseInventoryCardEffectsProps {
  fetchedHistoryHandler: (data: HistoryItem[]) => void;
  scrollHistoryToBottom: () => void;
}
export interface UseInventoryWsProps {
  onHistory: (records: ModelInventoryChange) => void;
  inventoryId: number;
}

export type HistoryType = FC<HistoryProps>;
export type ProductsType = FC<ProductsProps>;
export type HeaderType = FC<HeaderProps>;
export type FiltersType = FC<FiltersProps>;
